import { useEffect, useState } from 'react';
import { useAuth } from '../../shared/context/AuthContext';
import { applyOfflineViewState, isOfflineView } from '../../shared/utils/offlineUtils';

/**
 * @param {string | null} offlinePatientId
 * @param {(user: import('firebase/auth').User) => void} subscribeToPatientId
 * @param {(patientId: string) => void} setPatientId
 * @param {(resolved: boolean) => void} setPatientResolved
 * @returns {{
 *   authChecked: boolean,
 *   isDemoUser: boolean,
 *   isDemoOffline: boolean,
 *   userId: string | null,
 *   getIdToken: () => Promise<string | null>,
 * }}
 */
export function useDashboardAuthState(offlinePatientId, subscribeToPatientId, setPatientId, setPatientResolved) {
  const { auth, user, isDemo, isOffline, isAuthResolved } = useAuth();
  const [authChecked, setAuthChecked] = useState(false);
  const [isDemoUser, setIsDemoUser] = useState(false);
  const [isDemoOffline, setIsDemoOffline] = useState(false);
  const [userId, setUserId] = useState(/** @type {string | null} */ (null));

  useEffect(() => {
    if (!isAuthResolved) return;

    const offlineView = isOfflineView(offlinePatientId ? { offlinePatientId } : null);

    if (offlineView && offlinePatientId) {
      applyOfflineViewState({
        offlinePatientId,
        setIsDemoUser,
        setIsDemoOffline,
        setUserId,
        setPatientId,
        setPatientResolved,
        setAuthChecked,
      });
      return;
    }

    if (!user) {
      setIsDemoUser(false);
      setIsDemoOffline(false);
      setUserId(null);
      setAuthChecked(true);
      return;
    }

    setIsDemoUser(isDemo);
    setIsDemoOffline(isDemo && isOffline);
    setUserId(isDemo ? 'demo' : user.uid);
    subscribeToPatientId(user);
    setAuthChecked(true);
  }, [isAuthResolved, user, isDemo, isOffline, offlinePatientId, subscribeToPatientId, setPatientId, setPatientResolved]);

  // Mints a fresh ID token at call time (never cached) - reads auth.currentUser
  // directly rather than the `user` state above to avoid a stale closure.
  const getIdToken = () =>
    (auth?.currentUser ? auth.currentUser.getIdToken() : Promise.resolve(null));

  return {
    authChecked,
    isDemoUser,
    isDemoOffline,
    userId,
    getIdToken,
  };
}
